import React, { useEffect, useState, useCallback, useRef } from 'react';
import { api, trackEvent } from './App';
import { DragHandle, Check, Close, ReturnArrow, Calendar } from './Icons';
import { InfoTooltip } from './InfoTooltip';

const SESSION_TYPES = ['Module', 'Recall', 'MCQ', 'Test', 'Misc'];
const SESSION_TYPES_SIMPLIFIED = ['Study', 'MCQ', 'Recall', 'Review', 'Test'];
const SIMPLIFIED_LABEL_MAP = { Module: 'Study', Recall: 'Recall', MCQ: 'MCQ', Test: 'Test', Misc: 'Review' };
const SIMPLIFIED_VALUE_MAP = { Study: 'Module', MCQ: 'MCQ', Recall: 'Recall', Review: 'Misc', Test: 'Test' };
const TEST_PROVIDERS = ['BARBRI', 'SRA Papers', 'QLTS', 'Revise SQE', 'Law Drills'];
const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const WEEK_START = new Date('2026-03-30');

function getWeekDates(weekNum) {
  const start = new Date(WEEK_START);
  start.setDate(start.getDate() + (weekNum - 1) * 7);
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  const fmt = d => d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  return `${fmt(start)} – ${fmt(end)}`;
}

function getWeekDayDate(weekNum, dayIndex) {
  const d = new Date(WEEK_START);
  d.setDate(d.getDate() + (weekNum - 1) * 7 + dayIndex);
  return d;
}

function icsDate(date) {
  const pad = n => String(n).padStart(2, '0');
  return `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}`;
}

function generateIcs(weekNum, weekData, allTopics) {
  const slots = weekData[weekNum] || {};
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//SQETrack//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
  ];

  DAYS.forEach((day, di) => {
    (slots[day] || []).forEach(slot => {
      if (!slot.session_type) return;
      const topic = allTopics.find(t => t.id === slot.topic_id);
      const date = getWeekDayDate(weekNum, di);
      const dateStr = icsDate(date);

      let summary = 'SQETrack: ';
      let description = '';

      if (slot.session_type === 'Module' && topic) {
        summary += `${topic.subtopic} (${topic.module})`;
        const confPct = topic.confidence !== null ? Math.round(topic.confidence * 100) + '%' : 'unrated';
        description = `Topic: ${topic.subtopic} | ${topic.topic} | Confidence: ${confPct}`;
      } else if (slot.session_type === 'Recall') {
        summary += topic ? `Recall: ${topic.subtopic}` : 'Recall session';
        description = topic ? `Recall: write down everything you remember about ${topic.subtopic}` : 'Recall session';
      } else if (slot.session_type === 'MCQ') {
        summary += 'MCQ practice';
        description = 'MCQ practice';
      } else if (slot.session_type === 'Test') {
        summary += `Mock test${slot.notes ? ` (${slot.notes})` : ''}`;
        description = slot.notes ? `Provider: ${slot.notes}` : 'Mock test';
      } else if (slot.session_type === 'Misc') {
        summary += slot.notes || 'Revision session';
        description = slot.notes || 'Revision session';
      } else {
        summary += 'Revision session';
      }

      // Escape special characters for .ics
      const esc = s => s.replace(/[,;\\]/g, c => '\\' + c).replace(/\n/g, '\\n');

      lines.push('BEGIN:VEVENT');
      lines.push(`DTSTART;VALUE=DATE:${dateStr}`);
      lines.push(`DTEND;VALUE=DATE:${dateStr}`);
      lines.push(`SUMMARY:${esc(summary)}`);
      if (description) lines.push(`DESCRIPTION:${esc(description)}`);
      lines.push(`UID:sqetrack-${weekNum}-${di}-${slot.id || slot.slot}@sqetrack`);
      lines.push('END:VEVENT');
    });
  });

  lines.push('END:VCALENDAR');
  return lines.join('\r\n');
}

function downloadIcs(content, weekNum) {
  const blob = new Blob([content], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `SQETrack_Week${weekNum}.ics`;
  a.click();
  URL.revokeObjectURL(url);
}

function currentWeekNumber() {
  const now = new Date();
  const diff = Math.floor((now - WEEK_START) / (1000 * 60 * 60 * 24 * 7));
  return Math.max(1, diff + 1);
}

function todayDayIndex() {
  const d = new Date().getDay();
  return d === 0 ? 6 : d - 1;
}

// Default session type colours (overridable via settings)
const DEFAULT_SESSION_COLORS = {
  Test:   '#14b8a6',
  MCQ:    '#f43f5e',
  Recall: '#8b5cf6',
};

function hexToStyleColors(hex) {
  // Convert a hex color to the bg/border/text object used by slot cards
  return { bg: hex + '1f', border: hex + '66', text: hex };
}

function buildSessionColors(settings) {
  return {
    Test:   hexToStyleColors(settings.color_test   || DEFAULT_SESSION_COLORS.Test),
    MCQ:    hexToStyleColors(settings.color_mcq    || DEFAULT_SESSION_COLORS.MCQ),
    Recall: hexToStyleColors(settings.color_recall || DEFAULT_SESSION_COLORS.Recall),
    Misc:   'color_misc',
  };
}

// Review urgency colors (also user-configurable)
function buildReviewColors(settings) {
  const nw = settings.color_review_needs_work || '#e05555';
  const mo = settings.color_review_moderate   || '#d4903a';
  return {
    needs_work: { bg: nw + '14', border: nw + '59', badge: nw, label: <><ReturnArrow size={10} /> Review: needs work</> },
    moderate:   { bg: mo + '14', border: mo + '59', badge: mo, label: <><ReturnArrow size={10} /> Review: moderate</> },
  };
}

// Confidence-based colours for the left border stripe on cards with assigned topics
function confBorderColor(confidence) {
  if (confidence === null || confidence === undefined) return null;
  if (confidence < 0.5) return 'rgba(224,85,85,0.6)';
  if (confidence < 0.7) return 'rgba(212,144,58,0.6)';
  return 'rgba(76,175,125,0.6)';
}

// Static fallback review colours (overridden by buildReviewColors when settings load)
const DEFAULT_REVIEW_COLORS = {
  needs_work: { bg: 'rgba(224,85,85,0.08)', border: 'rgba(224,85,85,0.35)', badge: '#e05555', label: <><ReturnArrow size={10} /> Review: needs work</> },
  moderate:   { bg: 'rgba(212,144,58,0.08)', border: 'rgba(212,144,58,0.35)', badge: '#d4903a', label: <><ReturnArrow size={10} /> Review: moderate</> },
};

function TopicPickerDropdown({ topics, onSelect, onClose, anchorRect }) {
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  const filtered = topics.filter(t => {
    const q = search.toLowerCase();
    return !q || (t.subtopic||'').toLowerCase().includes(q) || (t.topic||'').toLowerCase().includes(q) || (t.module||'').toLowerCase().includes(q);
  }).slice(0, 60);

  const style = anchorRect ? {
    top: Math.min(anchorRect.bottom + 4, window.innerHeight - 300),
    left: Math.min(anchorRect.left, window.innerWidth - 290),
  } : { top: 100, left: 100 };

  return (
    <div ref={ref} className="picker-dropdown" style={style}>
      <input className="picker-search" placeholder="Search topics" autoFocus
        value={search} onChange={e => setSearch(e.target.value)} />
      <div className="picker-options">
        <div className="picker-option picker-none" onClick={() => { onSelect(null); onClose(); }}>— Remove topic —</div>
        {['FLK1','FLK2'].map(mod => {
          const modTopics = filtered.filter(t => t.module === mod);
          if (!modTopics.length) return null;
          return (
            <div key={mod}>
              <div className="picker-module-header" style={{ color: mod === 'FLK1' ? 'var(--flk1)' : 'var(--flk2)' }}>{mod}</div>
              {modTopics.map(t => (
                <div key={t.id} className="picker-option"
                  onClick={() => { onSelect(t.id, t.module); onClose(); }}>
                  <span className="picker-opt-name">{t.subtopic}</span>
                  <span className="picker-opt-meta">{t.topic}</span>
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SlotCard({ slot, topics, onUpdate, onRemove, onDragStart, onDragEnd, onNavigateToTopic, sessionColors, reviewColors, isMobile, onMoveUp, onMoveDown, onMoveLeft, onMoveRight, simplifiedMode, dayIndex }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [anchorRect, setAnchorRect] = useState(null);
  const cardRef = useRef(null);

  const topic = topics.find(t => t.id === slot.topic_id);
  const isMCQ = slot.session_type === 'MCQ';
  const isTest = slot.session_type === 'Test';
  const isModule = slot.session_type === 'Module';
  const isRecall = slot.session_type === 'Recall';
  const isMisc = slot.session_type === 'Misc';
  const topicModule = topic?.module;
  const testProvider = slot.notes || '';

  // Determine card background/border
  // Priority: review type > session type > neutral
  let cardStyle = {};
  const reviewType = slot.review_type; // 'needs_work' | 'moderate' | undefined

  const REVIEW_COLORS = reviewColors || DEFAULT_REVIEW_COLORS;
  const SESSION_TYPE_COLORS = sessionColors || {};
  if (reviewType && REVIEW_COLORS[reviewType]) {
    const rc = REVIEW_COLORS[reviewType];
    cardStyle = { background: rc.bg, borderColor: rc.border };
  } else if (!isModule && SESSION_TYPE_COLORS[slot.session_type]) {
    const sc = SESSION_TYPE_COLORS[slot.session_type];
    cardStyle = { background: sc.bg, borderColor: sc.border };
  }

  // Confidence stripe on left border when topic assigned (Module or Test only)
  const confStripe = !isMCQ && topic ? confBorderColor(topic.confidence) : null;
  if (confStripe) {
    cardStyle.borderLeftColor = confStripe;
    cardStyle.borderLeftWidth = '3px';
  }

  // Module colour accent in header
  const moduleTextColor = topicModule === 'FLK1' ? 'var(--flk1)' : topicModule === 'FLK2' ? 'var(--flk2)' : 'var(--muted)';

  const openPicker = e => {
    e.stopPropagation();
    const rect = cardRef.current?.getBoundingClientRect();
    setAnchorRect(rect);
    setPickerOpen(true);
  };

  const handleTopicClick = e => {
    e.stopPropagation();
    if (topic && onNavigateToTopic) {
      onNavigateToTopic(topic.id, topic.module);
    }
  };

  const typeColor = SESSION_TYPE_COLORS[slot.session_type]?.text || 'var(--muted)';

  return (
    <>
      <div
        ref={cardRef}
        className={`slot-card ${slot.completed ? 'slot-done' : ''}`}
        style={cardStyle}
        draggable
        onDragStart={e => { e.dataTransfer.effectAllowed = 'move'; onDragStart(slot); }}
        onDragEnd={onDragEnd}
      >
        <div className="slot-card-top">
          <span className="drag-handle"><DragHandle size={12} /></span>
          <select className="slot-type-select"
            value={simplifiedMode ? (SIMPLIFIED_LABEL_MAP[slot.session_type] || slot.session_type || '') : (slot.session_type || '')}
            style={{ color: isModule ? moduleTextColor : typeColor, fontWeight: 500 }}
            onChange={e => { const val = simplifiedMode ? (SIMPLIFIED_VALUE_MAP[e.target.value] || e.target.value) : e.target.value; onUpdate({ ...slot, session_type: val, topic_id: val === 'MCQ' ? null : slot.topic_id }); }}>
            <option value="">— Type —</option>
            {(simplifiedMode ? SESSION_TYPES_SIMPLIFIED : SESSION_TYPES).map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <div
            className={`slot-done-box ${slot.completed ? 'checked' : ''}`}
            onClick={() => onUpdate({ ...slot, completed: slot.completed ? 0 : 1 })}
          >{slot.completed ? <Check size={10} /> : ''}</div>
          <button className="slot-remove-btn" onClick={() => onRemove(slot)}><Close size={10} /></button>
        </div>

        {reviewType && REVIEW_COLORS[reviewType] && (
          <div className="slot-review-badge" style={{ color: REVIEW_COLORS[reviewType].badge, borderColor: REVIEW_COLORS[reviewType].badge + '55', background: REVIEW_COLORS[reviewType].bg }}>
            {REVIEW_COLORS[reviewType].label}
          </div>
        )}

        {isModule && (
          <div className="slot-topic-area" onClick={openPicker}>
            {topic ? (
              <div className="slot-topic-assigned">
                <span style={{ color: moduleTextColor, fontSize: 9, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{topic.module}</span>
                <span className="slot-topic-name slot-topic-link" onClick={handleTopicClick} title="Open in FLK tracker">
                  {topic.subtopic}
                </span>
                <span className="slot-topic-mod">{topic.topic}</span>
              </div>
            ) : (
              <button className="slot-assign-btn">+ Assign topic</button>
            )}
          </div>
        )}

        {isTest && (
          <div className="slot-topic-area">
            <select className="slot-provider-select"
              value={testProvider}
              onChange={e => onUpdate({ ...slot, notes: e.target.value })}>
              <option value="">— Select provider —</option>
              {TEST_PROVIDERS.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
        )}

        {isMCQ && (
          <div className="slot-mcq-label">MCQ practice</div>
        )}

        {isRecall && (
          <div className="slot-topic-area" onClick={openPicker}>
            {topic ? (
              <div className="slot-topic-assigned">
                <span style={{ color: topic.module === 'FLK1' ? 'var(--flk1)' : 'var(--flk2)', fontSize: 9, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{topic.module}</span>
                <span className="slot-topic-name" style={{ color: '#8b5cf6' }}>{topic.subtopic}</span>
                <span className="slot-topic-mod">{topic.topic}</span>
              </div>
            ) : (
              <button className="slot-assign-btn" style={{ color: '#8b5cf6' }}>+ Assign topic (optional)</button>
            )}
          </div>
        )}

        {isMisc && (
          <div className="slot-topic-area">
            <input
              className="slot-misc-input"
              type="text"
              placeholder="e.g. Review test results, revisit weak topic"
              value={slot.notes || ''}
              onChange={e => onUpdate({ ...slot, notes: e.target.value })}
              onClick={e => e.stopPropagation()}
            />
          </div>
        )}

        {isMobile && (
          <div className="slot-move-btns">
            <button className="slot-move-btn" onClick={e => { e.stopPropagation(); onMoveUp(slot); }} title="Move up">↑</button>
            <button className="slot-move-btn" onClick={e => { e.stopPropagation(); onMoveDown(slot); }} title="Move down">↓</button>
            {dayIndex > 0 && <button className="slot-move-btn slot-move-day" onClick={e => { e.stopPropagation(); onMoveLeft(slot); }} title={`Move to ${DAYS[dayIndex - 1]}`}>← {DAYS[dayIndex - 1]}</button>}
            {dayIndex < 6 && <button className="slot-move-btn slot-move-day" onClick={e => { e.stopPropagation(); onMoveRight(slot); }} title={`Move to ${DAYS[dayIndex + 1]}`}>{DAYS[dayIndex + 1]} →</button>}
          </div>
        )}
      </div>

      {pickerOpen && (
        <TopicPickerDropdown topics={topics} anchorRect={anchorRect}
          onSelect={tid => onUpdate({ ...slot, topic_id: tid })}
          onClose={() => setPickerOpen(false)} />
      )}
    </>
  );
}

function DayColumn({ day, dayIndex, slots, topics, onUpdate, onRemove, onAddSlot, onDrop, onDragStart, onDragEnd, onNavigateToTopic, sessionColors, reviewColors, isMobile, onMoveUp, onMoveDown, onMoveLeft, onMoveRight, simplifiedMode }) {
  const [dragOver, setDragOver] = useState(false);
  const isToday = dayIndex === todayDayIndex();

  return (
    <div className="day-col">
      <div className={`day-col-header ${isToday ? 'today' : ''}`}>
        {day}
        <span style={{ fontSize: 10, color: 'var(--hint)', fontFamily: 'var(--mono)' }}>{slots.length}</span>
      </div>
      <div
        className={`day-slots ${dragOver ? 'drag-over' : ''}`}
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={e => { e.preventDefault(); setDragOver(false); onDrop(day); }}
      >
        {slots.map(slot => (
          <SlotCard key={slot.id || `${slot.day}-${slot.slot}`}
            slot={slot} topics={topics}
            onUpdate={onUpdate} onRemove={onRemove}
            onDragStart={onDragStart} onDragEnd={onDragEnd}
            onNavigateToTopic={onNavigateToTopic}
            sessionColors={sessionColors}
            reviewColors={reviewColors}
            isMobile={isMobile}
            dayIndex={dayIndex}
            onMoveUp={slot => onMoveUp(slot)}
            onMoveDown={slot => onMoveDown(slot)}
            onMoveLeft={slot => onMoveLeft(slot)}
            onMoveRight={slot => onMoveRight(slot)}
            simplifiedMode={simplifiedMode} />
        ))}
      </div>
      <button className="add-slot-btn" onClick={() => onAddSlot(day)}>+</button>
    </div>
  );
}

function computeTarget(examDateStr, bufferDays, nonStrongCount) {
  if (!examDateStr) return null;
  const exam = new Date(examDateStr);
  const target = new Date(exam.getTime() - bufferDays * 86400000);
  const now = new Date();
  const weeksLeft = Math.max(1, Math.ceil((target - now) / (7 * 86400000)));
  return Math.max(3, Math.min(35, Math.ceil(nonStrongCount / weeksLeft)));
}

export default function Timetable({ onNavigateToTopic }) {
  const [allTopics, setAllTopics] = useState([]);
  const [weekData, setWeekData] = useState({});
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeWeek, setActiveWeek] = useState(currentWeekNumber);
  const [prefillCount, setPrefillCount] = useState('');
  const [sessionsPerDay, setSessionsPerDay] = useState(3);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const [mobileDay, setMobileDay] = useState(0); // 0=Mon, 1=Tue, etc.
  const dragSlot = useRef(null);
  const nextLocalId = useRef(-1);
  // Maps negative localId → Promise<realId> so updates/removes that arrive
  // before the INSERT finishes can wait for the real DB id.
  const pendingIds = useRef(new Map());

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  useEffect(() => {
    Promise.all([api('/api/topics/FLK1'), api('/api/topics/FLK2'), api('/api/timetable'), api('/api/settings')])
      .then(([flk1, flk2, tt, s]) => {
        setAllTopics([...flk1, ...flk2]);
        setSettings(s || {});
        const byWeek = {};
        tt.forEach(slot => {
          if (!byWeek[slot.week_number]) byWeek[slot.week_number] = {};
          if (!byWeek[slot.week_number][slot.day]) byWeek[slot.week_number][slot.day] = [];
          byWeek[slot.week_number][slot.day].push(slot);
        });
        setWeekData(byWeek);
        setLoading(false);
      });
  }, []);

  const getSlots = (week, day) => weekData[week]?.[day] || [];

  const persistSlot = useCallback(async slot => {
    return api('/api/timetable', { method: 'POST', body: JSON.stringify(slot) });
  }, []);

  const addSlot = useCallback((day, overrides = {}) => {
    const localId = nextLocalId.current--;
    const newSlot = {
      id: localId, week_number: activeWeek, day,
      slot: Math.floor(Date.now() / 1000) % 1000000, topic_id: null, session_type: '', completed: 0, notes: '',
      ...overrides,
    };
    setWeekData(prev => {
      const week = { ...(prev[activeWeek] || {}) };
      week[day] = [...(week[day] || []), newSlot];
      return { ...prev, [activeWeek]: week };
    });
    // Store promise so updateSlot/removeSlot can await the real DB id
    const idPromise = persistSlot(newSlot).then(res => {
      const realId = res?.id;
      if (realId) {
        setWeekData(prev => {
          const updated = {};
          Object.entries(prev).forEach(([wk, days]) => {
            updated[wk] = {};
            Object.entries(days).forEach(([d, slots]) => {
              updated[wk][d] = slots.map(s => s.id === localId ? { ...s, id: realId } : s);
            });
          });
          return updated;
        });
      }
      pendingIds.current.delete(localId);
      return realId;
    });
    pendingIds.current.set(localId, idPromise);
  }, [activeWeek, persistSlot]);

  const updateSlot = useCallback(updated => {
    // Always update local state immediately for responsiveness
    setWeekData(prev => {
      const week = { ...(prev[updated.week_number] || {}) };
      DAYS.forEach(d => {
        if (week[d]) week[d] = week[d].map(s => s.id === updated.id ? (d === updated.day ? updated : { ...updated, day: d }) : s);
      });
      return { ...prev, [updated.week_number]: week };
    });
    if (updated.id > 0) {
      persistSlot(updated);
    } else {
      // Slot still has a temporary negative id — wait for the real DB id
      const idPromise = pendingIds.current.get(updated.id);
      if (idPromise) {
        idPromise.then(realId => {
          if (realId) persistSlot({ ...updated, id: realId });
        });
      }
    }
  }, [persistSlot]);

  const removeSlot = useCallback(async slot => {
    setWeekData(prev => {
      const week = { ...(prev[slot.week_number] || {}) };
      DAYS.forEach(d => { if (week[d]) week[d] = week[d].filter(s => s.id !== slot.id); });
      return { ...prev, [slot.week_number]: week };
    });
    if (slot.id > 0) {
      await api('/api/timetable/delete', { method: 'POST', body: JSON.stringify({ id: slot.id }) });
    } else {
      // Slot still has a temporary negative id — wait then delete
      const idPromise = pendingIds.current.get(slot.id);
      if (idPromise) {
        idPromise.then(realId => {
          if (realId) api('/api/timetable/delete', { method: 'POST', body: JSON.stringify({ id: realId }) });
        });
        pendingIds.current.delete(slot.id);
      }
    }
  }, []);

  const handleDragStart = useCallback(slot => { dragSlot.current = slot; }, []);
  const handleDragEnd = useCallback(() => { dragSlot.current = null; }, []);

  const handleDrop = useCallback(targetDay => {
    const slot = dragSlot.current;
    if (!slot) return;
    const moved = { ...slot, day: targetDay };
    setWeekData(prev => {
      const week = { ...(prev[activeWeek] || {}) };
      DAYS.forEach(d => { if (week[d]) week[d] = week[d].filter(s => s.id !== slot.id); });
      week[targetDay] = [...(week[targetDay] || []), moved];
      return { ...prev, [activeWeek]: week };
    });
    persistSlot(moved);
  }, [activeWeek, persistSlot]);

  const moveSlotUp = useCallback((slot) => {
    setWeekData(prev => {
      const week = { ...(prev[activeWeek] || {}) };
      const daySlots = [...(week[slot.day] || [])];
      const idx = daySlots.findIndex(s => s.id === slot.id);
      if (idx <= 0) return prev;
      [daySlots[idx - 1], daySlots[idx]] = [daySlots[idx], daySlots[idx - 1]];
      return { ...prev, [activeWeek]: { ...week, [slot.day]: daySlots } };
    });
  }, [activeWeek]);

  const moveSlotDown = useCallback((slot) => {
    setWeekData(prev => {
      const week = { ...(prev[activeWeek] || {}) };
      const daySlots = [...(week[slot.day] || [])];
      const idx = daySlots.findIndex(s => s.id === slot.id);
      if (idx < 0 || idx >= daySlots.length - 1) return prev;
      [daySlots[idx], daySlots[idx + 1]] = [daySlots[idx + 1], daySlots[idx]];
      return { ...prev, [activeWeek]: { ...week, [slot.day]: daySlots } };
    });
  }, [activeWeek]);

  const moveSlotLeft = useCallback((slot) => {
    const dayIdx = DAYS.indexOf(slot.day);
    if (dayIdx <= 0) return;
    const newDay = DAYS[dayIdx - 1];
    const moved = { ...slot, day: newDay };
    setWeekData(prev => {
      const week = { ...(prev[activeWeek] || {}) };
      DAYS.forEach(d => { if (week[d]) week[d] = week[d].filter(s => s.id !== slot.id); });
      week[newDay] = [...(week[newDay] || []), moved];
      return { ...prev, [activeWeek]: week };
    });
    if (moved.id > 0) { persistSlot(moved); }
    else { const p = pendingIds.current.get(moved.id); if (p) p.then(rid => { if (rid) persistSlot({ ...moved, id: rid }); }); }
    // On mobile single-day view, follow the card to the new day
    if (isMobile) setMobileDay(dayIdx - 1);
  }, [activeWeek, persistSlot, isMobile]);

  const moveSlotRight = useCallback((slot) => {
    const dayIdx = DAYS.indexOf(slot.day);
    if (dayIdx >= DAYS.length - 1) return;
    const newDay = DAYS[dayIdx + 1];
    const moved = { ...slot, day: newDay };
    setWeekData(prev => {
      const week = { ...(prev[activeWeek] || {}) };
      DAYS.forEach(d => { if (week[d]) week[d] = week[d].filter(s => s.id !== slot.id); });
      week[newDay] = [...(week[newDay] || []), moved];
      return { ...prev, [activeWeek]: week };
    });
    if (moved.id > 0) { persistSlot(moved); }
    else { const p = pendingIds.current.get(moved.id); if (p) p.then(rid => { if (rid) persistSlot({ ...moved, id: rid }); }); }
    // On mobile single-day view, follow the card to the new day
    if (isMobile) setMobileDay(dayIdx + 1);
  }, [activeWeek, persistSlot, isMobile]);

  const handlePrefill = async (nOverride) => {
    const n = nOverride !== undefined ? nOverride : parseInt(prefillCount);
    if (!n || n < 1 || n > 50) return;
    trackEvent('first_planner_autofill', { week: activeWeek, slots: n });
    const suggestions = await api(`/api/review-suggestions/${activeWeek}`);

    // Count existing slots per day
    const existingPerDay = {};
    DAYS.forEach(d => { existingPerDay[d] = getSlots(activeWeek, d).length; });

    // Build ordered list of day slots to fill (prioritise days with fewer sessions)
    const slotsToAdd = [];
    let weekdayIdx = 0;
    const WEEKDAYS = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
    let attempts = 0;
    while (slotsToAdd.length < n && attempts < n * 7) {
      attempts++;
      const day = WEEKDAYS[weekdayIdx % WEEKDAYS.length];
      weekdayIdx++;
      if ((existingPerDay[day] || 0) < sessionsPerDay) {
        slotsToAdd.push(day);
        existingPerDay[day] = (existingPerDay[day] || 0) + 1;
      }
    }

    // Priority order: weak (<50%), then moderate (50-69%), then suggestions
    const weakTopics = allTopics.filter(t => t.confidence !== null && t.confidence < 0.5)
      .sort((a, b) => (a.confidence ?? 0) - (b.confidence ?? 0));
    const moderateTopics = allTopics.filter(t => t.confidence !== null && t.confidence >= 0.5 && t.confidence < 0.7)
      .sort((a, b) => (a.confidence ?? 0) - (b.confidence ?? 0));
    const priorityQueue = [...weakTopics, ...moderateTopics, ...suggestions];

    let suggIdx = 0;
    let mcqAddedDays = new Set();

    for (let i = 0; i < slotsToAdd.length; i++) {
      const day = slotsToAdd[i];
      // Add one MCQ per day if not already added
      if (!mcqAddedDays.has(day) && i > 0) {
        mcqAddedDays.add(day);
        addSlot(day, { session_type: 'MCQ' });
      } else {
        const suggestion = priorityQueue[suggIdx];
        const isWeak = suggestion && suggestion.confidence !== null && suggestion.confidence < 0.5;
        const overrides = suggestion ? {
          topic_id: suggestion.id,
          session_type: 'Module',
          review_type: isWeak ? 'needs_work' : suggestion.confidence !== null && suggestion.confidence < 0.7 ? 'moderate' : undefined,
        } : { session_type: 'Module' };
        if (suggestion) suggIdx++;
        addSlot(day, overrides);
      }
      await new Promise(r => setTimeout(r, 30));
    }
    setPrefillCount('');
  };

  if (loading) return <div className="loading">Loading planner</div>;

  const sessionColors = buildSessionColors(settings);
  const reviewColors = buildReviewColors(settings);

  const currentWeekSlots = weekData[activeWeek] || {};
  const totalSlots = DAYS.reduce((s, d) => s + (currentWeekSlots[d]?.length || 0), 0);
  const totalDone = DAYS.reduce((s, d) => s + (currentWeekSlots[d]?.filter(sl => sl.completed).length || 0), 0);
  const needsWorkCount = DAYS.reduce((s, d) => s + (currentWeekSlots[d]?.filter(sl => sl.review_type === 'needs_work').length || 0), 0);
  const moderateCount = DAYS.reduce((s, d) => s + (currentWeekSlots[d]?.filter(sl => sl.review_type === 'moderate').length || 0), 0);

  const typeCounts = {};
  DAYS.forEach(d => {
    (currentWeekSlots[d] || []).forEach(sl => {
      if (sl.session_type) typeCounts[sl.session_type] = (typeCounts[sl.session_type] || 0) + 1;
    });
  });

  // Target-based auto-fill calculations
  const targetConf = parseFloat(settings.target_confidence) || 0.70;
  const nonStrongCount = allTopics.filter(t => t.confidence === null || t.confidence < targetConf).length;
  const strongCount = allTopics.filter(t => t.confidence !== null && t.confidence >= targetConf).length;
  const now = new Date();
  const examDateRaw = [settings.flk1_exam_date, settings.flk2_exam_date].filter(Boolean)
    .filter(d => new Date(d) > now)
    .sort()[0];
  const dynamicExamDate = [settings.flk1_exam_date, settings.flk2_exam_date]
    .filter(Boolean).filter(d => new Date(d) > new Date()).sort()[0];
  const totalWeeks = dynamicExamDate
    ? Math.max(17, Math.ceil((new Date(dynamicExamDate) - WEEK_START) / (7 * 86400000)) + 1)
    : 17;
  const t2w = examDateRaw ? computeTarget(examDateRaw, 14, nonStrongCount) : null;
  const t1w = examDateRaw ? computeTarget(examDateRaw, 7, nonStrongCount) : null;
  const t0w = examDateRaw ? computeTarget(examDateRaw, 0, nonStrongCount) : null;
  const totalTopics = allTopics.length;
  const strongPct = totalTopics > 0 ? Math.round(strongCount / totalTopics * 100) : 0;

  // On-track metric
  const coveredTopics = allTopics.filter(t => t.confidence !== null).length;
  const elapsedWeeks = Math.max(0, Math.floor((now - WEEK_START) / (7 * 86400000)));
  const weeksToExam = examDateRaw ? Math.max(0, Math.ceil((new Date(examDateRaw) - now) / (7 * 86400000))) : null;
  const totalStudyWeeks = examDateRaw ? Math.max(1, Math.round((new Date(examDateRaw) - WEEK_START) / (7 * 86400000))) : null;
  const expectedCoverage = (totalStudyWeeks && elapsedWeeks > 0) ? Math.round((elapsedWeeks / totalStudyWeeks) * totalTopics) : null;
  const onTrackRatio = (expectedCoverage && expectedCoverage > 0) ? coveredTopics / expectedCoverage : null;
  const onTrackStatus = onTrackRatio === null ? null : onTrackRatio >= 0.90 ? 'green' : onTrackRatio >= 0.70 ? 'amber' : 'red';
  const GREEN_COPY = ['On pace.', 'Right on schedule.', 'Solid progress this week.', 'Ahead of schedule.'];
  const greenCopy = GREEN_COPY[new Date().getDay() % GREEN_COPY.length];

  // Tooltip text for target buttons
  const fmtDate = d => d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  const tip2w = examDateRaw ? `Add this many sessions to finish studying by ${fmtDate(new Date(new Date(examDateRaw).getTime() - 14 * 86400000))}, 2 weeks before your exam` : 'Finish studying 2 weeks before exam';
  const tip1w = examDateRaw ? `Add this many sessions to finish studying by ${fmtDate(new Date(new Date(examDateRaw).getTime() - 7 * 86400000))}, 1 week before your exam` : 'Finish studying 1 week before exam';
  const tip0w = examDateRaw ? `Add this many sessions to finish studying by exam day (${fmtDate(new Date(examDateRaw))})` : 'Finish studying by exam day';

  return (
    <div className="timetable-page">
      <h1 className="page-heading">Weekly planner</h1>
      <div className="week-picker">
        {Array.from({ length: totalWeeks }, (_, i) => i + 1).map(w => {
          const wSlots = weekData[w] || {};
          const done = DAYS.reduce((s, d) => s + (wSlots[d]?.filter(sl => sl.completed).length || 0), 0);
          const total = DAYS.reduce((s, d) => s + (wSlots[d]?.length || 0), 0);
          const isCurrent = w === currentWeekNumber();
          return (
            <button key={w}
              className={`week-pill ${activeWeek === w ? 'active' : ''} ${done > 0 && done >= total && total > 0 ? 'week-pill-done' : ''} ${isCurrent && activeWeek !== w ? 'week-pill-current' : ''}`}
              onClick={() => setActiveWeek(w)} title={getWeekDates(w)}>
              W{w}
            </button>
          );
        })}
      </div>

      <div className="week-summary-bar">
        <div>
          <div className="week-summary-title">Week {activeWeek}</div>
          <div className="week-summary-dates">{getWeekDates(activeWeek)}</div>
        </div>
        <div className="week-summary-stats">
          <span className="wsstat"><span style={{ color: 'var(--green)' }}>{totalDone}</span>/{totalSlots} done</span>
          {needsWorkCount > 0 && <span className="wsstat-type" style={{ color: '#e05555', background: 'rgba(224,85,85,0.1)', borderColor: 'rgba(224,85,85,0.3)' }}><ReturnArrow size={10} /> {needsWorkCount} needs work</span>}
          {moderateCount > 0 && <span className="wsstat-type" style={{ color: '#d4903a', background: 'rgba(212,144,58,0.1)', borderColor: 'rgba(212,144,58,0.3)' }}><ReturnArrow size={10} /> {moderateCount} moderate</span>}
          {Object.entries(typeCounts).map(([type, count]) => {
            const c = sessionColors[type];
            return c && typeof c === 'object' ? <span key={type} className="wsstat-type" style={{ color: c.text, background: c.bg, borderColor: c.border }}>{type} ×{count}</span> : null;
          })}
        </div>
        <div className="prefill-row">
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginRight: 8 }}>
            <label style={{ fontSize: 11, color: 'var(--muted)', whiteSpace: 'nowrap' }}>Sessions/day:</label>
            <input
              className="prefill-input"
              type="number" min="1" max="8"
              value={sessionsPerDay}
              onChange={e => setSessionsPerDay(Math.max(1, Math.min(8, parseInt(e.target.value) || 3)))}
              style={{ width: 48 }}
              title="Maximum sessions to schedule per day"
            />
          </div>
          {t2w !== null ? (
            <div className="target-btn-group">
              <div className="target-group-wrap">
                <span className="target-label">Weekly topic target <InfoTooltip text="The number of topics to cover this week to stay on pace for your exam date. Based on your remaining topics and sessions per day." /></span>
                <span className="target-hint">How many topics to cover this week to reach your preparation target on time. Choose your preferred pace.</span>
              </div>
              <button className="target-btn" onClick={() => handlePrefill(t2w)} title={tip2w}>
                <span className="target-btn-n">{t2w}</span>
                <span className="target-btn-topics" style={{ fontSize: 9, color: 'var(--muted)', fontWeight: 400 }}>topics/week</span>
                <span className="target-btn-sub">Finish 2 wks early</span>
              </button>
              <button className="target-btn target-btn-mid" onClick={() => handlePrefill(t1w)} title={tip1w}>
                <span className="target-btn-n">{t1w}</span>
                <span className="target-btn-topics" style={{ fontSize: 9, color: 'var(--muted)', fontWeight: 400 }}>topics/week</span>
                <span className="target-btn-sub">Finish 1 wk early</span>
              </button>
              <button className="target-btn" onClick={() => handlePrefill(t0w)} title={tip0w}>
                <span className="target-btn-n">{t0w}</span>
                <span className="target-btn-topics" style={{ fontSize: 9, color: 'var(--muted)', fontWeight: 400 }}>topics/week</span>
                <span className="target-btn-sub">Finish by exam day</span>
              </button>
            </div>
          ) : (
            <>
              <input className="prefill-input" type="number" min="1" max="50" placeholder="# sessions"
                value={prefillCount} onChange={e => setPrefillCount(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handlePrefill()} />
              <button className="prefill-btn" onClick={() => handlePrefill()}>Auto-fill week</button>{' '}<InfoTooltip text="Populates your week with priority topics from your dashboard. Includes recall sessions for stale topics. You can rearrange or remove anything it adds." />
            </>
          )}
          <button
            className="cal-export-btn"
            onClick={() => downloadIcs(generateIcs(activeWeek, weekData, allTopics), activeWeek)}
            title="Download this week as a calendar file (.ics)"
          >
            <Calendar size={13} /> Export to calendar
          </button>
        </div>
        {totalTopics > 0 && (
          <div className="plan-progress-wrap">
            <div className="plan-progress-bar-bg">
              <div className="plan-progress-bar-fill" style={{ width: strongPct + '%' }} />
            </div>
            <span className="plan-progress-text">{strongCount}/{totalTopics} topics at target ({strongPct}%) <InfoTooltip text="Shows how many of your topics are at your target confidence." /></span>
          </div>
        )}
        {onTrackStatus && (
          <div className={`ontrack-badge ontrack-${onTrackStatus}`}
            title="Tracks how many topics you have rated so far compared to your target pace. Rating a topic means you have set your confidence on it, even if the level is low.">
            {onTrackStatus === 'green' && <>On track. {strongCount} of {totalTopics} topics at target. {nonStrongCount} remaining. {greenCopy}</>}
            {onTrackStatus === 'amber' && <>Slightly behind. {coveredTopics} of {totalTopics} topics covered. Cover {Math.ceil((expectedCoverage - coveredTopics) / Math.max(1, weeksToExam))} per week to catch up.</>}
            {onTrackStatus === 'red' && (
              coveredTopics === 0
                ? <>No topics rated yet. Start with your FLK1 topics to get the dashboard working.</>
                : coveredTopics <= 30
                  ? <>Getting started. {coveredTopics} of {totalTopics} topics rated. Rate more to sharpen your priorities.</>
                  : <>Behind pace. {nonStrongCount} topics still below target. Cover {Math.ceil((expectedCoverage ? expectedCoverage - coveredTopics : nonStrongCount) / Math.max(1, weeksToExam || 4))} per week to stay on track.</>
            )}
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="planner-legend">
        <span className="legend-item"><span className="legend-swatch" style={{ background: 'rgba(139,92,246,0.3)', borderColor: 'rgba(139,92,246,0.5)' }} />Recall</span>
        <span className="legend-item"><span className="legend-swatch" style={{ background: 'rgba(244,63,94,0.3)', borderColor: 'rgba(244,63,94,0.5)' }} />MCQ</span>
        <span className="legend-item"><span className="legend-swatch" style={{ background: 'rgba(20,184,166,0.3)', borderColor: 'rgba(20,184,166,0.5)' }} />Test</span>
        <span className="legend-item"><span className="legend-swatch" style={{ background: 'rgba(224,85,85,0.2)', borderColor: 'rgba(224,85,85,0.5)' }} />Review: needs work</span>
        <span className="legend-item"><span className="legend-swatch" style={{ background: 'rgba(212,144,58,0.2)', borderColor: 'rgba(212,144,58,0.5)' }} />Review: moderate</span>
        <span className="legend-item"><span className="legend-swatch conf-swatch-red" />Low confidence</span>
        <span className="legend-item"><span className="legend-swatch conf-swatch-amber" />Mid confidence</span>
        <span className="legend-item"><span className="legend-swatch conf-swatch-green" />High confidence</span>
      </div>

      {isMobile && (
        <div className="planner-day-picker">
          {DAYS.map((day, di) => (
            <button key={day}
              className={`planner-day-pill${mobileDay === di ? ' active' : ''}`}
              onClick={() => setMobileDay(di)}>
              {day}
            </button>
          ))}
        </div>
      )}

      <div className={`days-board${isMobile ? ' days-board-single' : ''}`}>
        {(isMobile ? [DAYS[mobileDay]] : DAYS).map((day) => {
          const di = DAYS.indexOf(day);
          return (
            <DayColumn key={day} day={day} dayIndex={di}
              slots={getSlots(activeWeek, day)}
              topics={allTopics}
              onUpdate={updateSlot} onRemove={removeSlot}
              onAddSlot={addSlot} onDrop={handleDrop}
              onDragStart={handleDragStart} onDragEnd={handleDragEnd}
              onNavigateToTopic={onNavigateToTopic}
              sessionColors={sessionColors}
              reviewColors={reviewColors}
              isMobile={isMobile}
              onMoveUp={moveSlotUp}
              onMoveDown={moveSlotDown}
              onMoveLeft={moveSlotLeft}
              onMoveRight={moveSlotRight}
              simplifiedMode={settings.planner_session_colours === 'simplified'} />
          );
        })}
      </div>
    </div>
  );
}
